import { useState } from 'react';
import { useCreateEntry } from '@hooks/useCreateEntry';
import { store } from '@store';
import compact from 'lodash/compact';

import style from './Right.module.scss';

import { Button, ButtonTheme } from '@components/Button';
import { SelectButtonMenuItem } from '@components/SelectButtonMenu';
import { Tooltip, TooltipPlace, TooltipType } from '@components/Tooltip';

import { SelectButtonList } from '@components/SelectButtonList';
import { SelectButtonListModifiers } from '@components/SelectButtonList/SelectButtonList';
import { CommonTestAttributes, EhrPageAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

export interface RightContentParams {
    showReplenishButton?: boolean;
    showPrintButton?: boolean;
    showPrintTitleListButton?: boolean;
}

interface RightContentProps extends RightContentParams {
    hoverText?: string;
    onReplenishClick(): void;
    onPrintClick(): void;
    onPrintTitleListClick(): void;
    onRemoveNotExistEntriesClick?(): void;
}

export function RightContent({
    hoverText,
    showReplenishButton,
    showPrintButton,
    showPrintTitleListButton,
    onPrintTitleListClick,
    onReplenishClick,
    onPrintClick,
}: RightContentProps) {
    const [isOnCreate, setIsOnCreate] = useState(false);
    const { app, authUser, ehr } = store;
    const { proceedEntryCreating } = useCreateEntry();

    const onCreateClick = async () => {
        if (!isOnCreate) {
            setIsOnCreate(true);
            if (!authUser.organizationId) {
                app.setSelectOrganizationPopupParams({
                    onSaveClick: () =>
                        proceedEntryCreating({
                            createParams: {
                                ehrId: ehr.id,
                            },
                            patientId: ehr.id,
                            recordId: ehr.recordId,
                        }),
                });
            } else {
                await proceedEntryCreating({
                    createParams: {
                        ehrId: ehr.id,
                    },
                    patientId: ehr.id,
                    recordId: ehr.recordId,
                });
            }
            setIsOnCreate(false);
        }
    };

    const buttons = getButtons({
        showReplenishButton,
        showPrintButton,
        showPrintTitleListButton,
        onCreateClick,
        onReplenishClick,
        onPrintClick,
        onPrintTitleListClick,
    });

    return (
        <div className={style.root}>
            <Tooltip
                title={hoverText ?? ''}
                place={TooltipPlace.Left}
                type={TooltipType.Dark}
                disable={!hoverText}
            >
                <div className={style.buttonsWrapper}>
                    {buttons.map(({ label, theme, onClick }, key) => (
                        <Button
                            dataTest={EhrPageAttributes.HeaderButtons}
                            theme={theme}
                            onClick={onClick}
                            key={key}
                        >
                            {label}
                        </Button>
                    ))}
                </div>
            </Tooltip>
            <div className={style.buttonsWrapper_mobile}>
                <SelectButtonList
                    dataTest={EhrPageAttributes.HeaderButtons}
                    items={buttons}
                    modifiers={[SelectButtonListModifiers.Single, SelectButtonListModifiers.Header]}
                />
            </div>
        </div>
    );
}

function getButtons({
    showReplenishButton,
    showPrintButton,
    showPrintTitleListButton,
    onCreateClick,
    onReplenishClick,
    onPrintClick,
    onPrintTitleListClick,
    onRemoveNotExistEntriesClick,
}: Omit<RightContentProps, 'hoverText'> & { onCreateClick(): void }): (SelectButtonMenuItem & {
    theme: ButtonTheme;
})[] {
    return compact([
        {
            label: translate('addEntry'),
            dataTest: CommonTestAttributes.ActionsCreateEntry,
            theme: ButtonTheme.Primary,
            onClick: onCreateClick,
        },
        showReplenishButton && {
            label: translate('topUpAccount'),
            dataTest: EhrPageAttributes.ReplenishButton,
            theme: ButtonTheme.Secondary,
            onClick: onReplenishClick,
        },
        showPrintButton && {
            label: translate('print'),
            dataTest: EhrPageAttributes.PrintButton,
            theme: ButtonTheme.Secondary,
            onClick: onPrintClick,
        },
        showPrintTitleListButton && {
            label: translate('printCoverPage'),
            dataTest: EhrPageAttributes.PrintTitleButton,
            theme: ButtonTheme.Secondary,
            onClick: onPrintTitleListClick,
        },
        onRemoveNotExistEntriesClick && {
            label: translate('clearElastic'),
            dataTest: EhrPageAttributes.RemoveNotExistEntries,
            theme: ButtonTheme.Secondary,
            onClick: onRemoveNotExistEntriesClick,
        },
    ]);
}
